import { useSubscriber } from "@hooks/query/useSubscriber";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import TabPanels from "@components/form/TabPanels";
import NotesPanel from "@components/notes/NotesPanel";
import NotificationHistoryTab from "@components/form/subscriber/dashboard/subscriber_history/NotificationHistoryTab";
import EvidenceTable from "@components/table/EvidenceTable";
import Loading from "@components/shared/Loading";
import { useAppContext } from "@context/AppContext";
import EventTransactionPaginatedTable from "@components/table/EventTransactionPaginatedTable";

const SubscriberAuditTrailsAndEvidences: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { checkPermissions } = useAppContext();
  const { id: subscriberId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { data: subscriber = {}, isLoading } = useSubscriber(subscriberId);

  if (isLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  const tabs = [
    ...(checkPermissions(["ViewEventTransactions"])
      ? [
          {
            key: "audit",
            title: t("subscriber.audit_trail"),
            panel: (
              <EventTransactionPaginatedTable subscriberId={subscriber.id} />
            ),
          },
        ]
      : []),
    ...(checkPermissions(["ViewEvidences"])
      ? [
          {
            key: "evidence",
            title: t("evidence.documents"),
            panel: <EvidenceTable subscriberId={subscriberId} />,
          },
        ]
      : []),
    ...(checkPermissions(["ViewNotes"])
      ? [
          {
            key: "notes",
            title: t("subscriber.notes"),
            panel: (
              <NotesPanel
                subscriberId={subscriberId}
                artifactName={subscriber.name}
              />
            ),
          },
        ]
      : []),
    {
      key: "notification",
      title: t("notification.title"),
      panel: <NotificationHistoryTab walletId={subscriber.walletId} />,
    },
  ];

  // Honour the `?tab=<key>` deep-link from listing-row badges so clicking the
  // notes icon lands on the Notes tab. Permission filtering above means a
  // simple numeric index would be brittle.
  const requestedTab = searchParams.get("tab");
  const initialTab = requestedTab
    ? tabs.findIndex(tab => tab.key === requestedTab)
    : 0;

  return <TabPanels selectedTab={initialTab >= 0 ? initialTab : 0} tabs={tabs} />;
};

export default SubscriberAuditTrailsAndEvidences;
